import * as THREE from 'three';
import rotationIcon from './assets/round-arrow.svg';
import type { IPointerController, IPointerHandler } from '@/components/viewPort/PointerController';
import type { SyntheticEvent } from '@/components/viewPort/SyntheticEvent';
import type { IBaseViewModel, IPickable, IProjectorApi } from '../../interfaces';
import type { ImagesService } from './Images.service';
import type { SliceType } from './interfaces';
import type { ICursorData, IMprCursorDataProvider } from './PointerHandlerAdjustMpr';
import type { PointerSyntheticEvent } from '@/components/viewPort/PointerSyntheticEvent';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';

import { MprModes, PointerHandlerAdjustMpr } from './PointerHandlerAdjustMpr';
import { ImagesMode } from './interfaces';

export enum PickedTypes {
  NONE = 1,
  UNAVAILABLE = 2,
  CROSS = 4,
  AXIS_HOR = 8,
  AXIS_VERT = 16,
  AXIS_RL = 32,
  AXIS_LR = 64
}

const allPickedTypesMask: number =
  PickedTypes.UNAVAILABLE |
  PickedTypes.CROSS |
  PickedTypes.AXIS_HOR |
  PickedTypes.AXIS_VERT |
  PickedTypes.AXIS_RL |
  PickedTypes.AXIS_LR;

const rotationsPickedTypesMask: number =
  PickedTypes.AXIS_HOR | PickedTypes.AXIS_VERT | PickedTypes.AXIS_RL | PickedTypes.AXIS_LR;

interface _IParams {
  rotationFreeAreaRadius2: number;
  crossStickDistanceThreshold2: [number, number];
  axisStickDistanceThreshold2: number;
}

export class MprAxisPicker2d implements IPickable, IMprCursorDataProvider {
  private readonly _imagesService: ImagesService;

  private readonly _projector: IProjectorApi;

  private readonly _mainSliceType: SliceType;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _moveCur: string;

  private readonly _rotationCur: string;

  private readonly _epsilon = 0.0001;

  private _params: _IParams;

  private _pickedType: PickedTypes = PickedTypes.NONE;

  private _allowedPickedTypesMask: number = allPickedTypesMask;

  public constructor(
    imagesService: ImagesService,
    projector: IProjectorApi,
    mainSliceType: SliceType,
    loggerService: ObservabilityLoggerService,
    moveCursor?: string,
    rotateCursor?: string
  ) {
    this._imagesService = imagesService;
    this._projector = projector;
    this._mainSliceType = mainSliceType;
    this._moveCur = moveCursor ?? 'move';
    this._rotationCur = rotateCursor ?? `url(${rotationIcon}) 12 12, auto`;
    this._params = { rotationFreeAreaRadius2: 0, crossStickDistanceThreshold2: [0, 0], axisStickDistanceThreshold2: 0 };
    this._loggerService = loggerService;
    this.setParams();
  }

  public setParams(
    rotationFreeAreaRadius = 50,
    crossStickDistanceThreshold = [50, 50],
    axisStickDistanceThreshold2 = 5
  ): void {
    this._params.rotationFreeAreaRadius2 = rotationFreeAreaRadius * rotationFreeAreaRadius;
    this._params.crossStickDistanceThreshold2 = [
      crossStickDistanceThreshold[0] * crossStickDistanceThreshold[0],
      crossStickDistanceThreshold[1] * crossStickDistanceThreshold[1]
    ];
    this._params.axisStickDistanceThreshold2 = axisStickDistanceThreshold2 * axisStickDistanceThreshold2;
  }

  public isPickingPoint(
    point: THREE.Vector3,
    rotationFreeAreaRadius?: number,
    axisStickDistanceThreshold?: number
  ): boolean {
    const cursor2D = this._projector.project(point);
    const cursor3D = this._projector.unproject(cursor2D);
    const cursorData = this._getCursorData(cursor2D, cursor3D);
    const pickedType = this._getPickedType(cursorData, rotationFreeAreaRadius, axisStickDistanceThreshold);
    return (pickedType & this._allowedPickedTypesMask) == pickedType;
  }

  public getViewModel(): IBaseViewModel | undefined {
    return undefined;
  }

  public getPickedDistance(event: SyntheticEvent<PointerEvent>): number | undefined {
    const cursorData = this.getCursorData(event);
    const pickedType = this._getPickedType(cursorData);
    if (this._pickedType == PickedTypes.NONE) {
      return (pickedType & this._allowedPickedTypesMask) == pickedType ? -1 : undefined;
    } else {
      return pickedType === this._pickedType ? -1 : undefined;
    }
  }

  public onPicked(event: PointerSyntheticEvent, parent: IPointerController | undefined): IPointerHandler | undefined {
    const cursorData = this.getCursorData(event);
    this._pickedType = this._getPickedType(cursorData);

    if (this._imagesService.imagesMode === ImagesMode.POLYLINE) {
      if (
        this._mainSliceType !== this._imagesService.getPolylineInfo()?.slice ||
        (this._pickedType & rotationsPickedTypesMask) !== this._pickedType
      ) {
        return;
      }
    }

    parent?.setCursor(this._getCursor(this._pickedType));

    return this._getPointerHandler(cursorData, this._pickedType);
  }

  public onUnPicked(parent: IPointerController | undefined): void {
    this._pickedType = PickedTypes.NONE;
    parent?.setCursor('');
  }

  private _getCursor(pickedType: PickedTypes): string {
    if (pickedType === PickedTypes.NONE) {
      return '';
    } else if (pickedType === PickedTypes.CROSS) {
      return this._moveCur;
    } else if (pickedType === PickedTypes.UNAVAILABLE) {
      return 'not-allowed';
    } else if (pickedType === PickedTypes.AXIS_VERT) {
      return this._rotationCur;
    } else if (pickedType === PickedTypes.AXIS_HOR) {
      return this._rotationCur;
    } else if (pickedType === PickedTypes.AXIS_RL) {
      return this._rotationCur;
    } else if (pickedType === PickedTypes.AXIS_LR) {
      return this._rotationCur;
    }
    return '';
  }

  private _getPointerHandler(_: ICursorData, pickedType: PickedTypes): IPointerHandler | undefined {
    if (pickedType === PickedTypes.NONE || pickedType === PickedTypes.UNAVAILABLE) {
      return undefined;
    }

    return new PointerHandlerAdjustMpr(
      this._imagesService,
      pickedType === PickedTypes.CROSS ? MprModes.MOVE : MprModes.ROTATE,
      this._mainSliceType,
      this,
      this._loggerService
    );
  }

  public getCursorData(event: SyntheticEvent<PointerEvent>): ICursorData {
    const cursor2D = new THREE.Vector2(event.originalEvent.offsetX, event.originalEvent.offsetY);

    const cursor3D = this._projector.unproject(cursor2D);

    return this._getCursorData(cursor2D, cursor3D);
  }

  private _getCursorData(cursor2D: THREE.Vector2, cursor3D: THREE.Vector3): ICursorData {
    const content = this._imagesService.getSlicePositionInfo(this._mainSliceType);

    let cursorPoint = new THREE.Vector3();
    if (content) {
      cursorPoint = cursor3D.clone().applyMatrix4(content.sliceCSInv);
      cursorPoint.z = 0;
      cursorPoint.applyMatrix4(content.sliceCS);
    }

    return {
      cursorPoint,
      cursor2D,
      cursor3D
    };
  }

  private _getPickedType(
    cursorData: ICursorData,
    rotationFreeAreaRadius?: number,
    axisStickDistanceThreshold?: number
  ): PickedTypes {
    let pickedType: PickedTypes = PickedTypes.NONE;
    const distances: Array<{ dist2: number; dir: THREE.Vector2 }> = [];
    const sliceTypes = this._imagesService.getRegisteredSliceTypes();
    sliceTypes.forEach((sliceType) => {
      if (sliceType != this._mainSliceType) {
        const content = this._imagesService.getSlicePositionInfo(sliceType);
        if (content) {
          const proj = cursorData.cursor3D.clone().applyMatrix4(content.sliceCSInv);
          proj.z = 0;
          proj.applyMatrix4(content.sliceCS);
          const proj2D = this._projector.project(proj);
          const dist = Math.abs(cursorData.cursor2D.distanceTo(proj2D));
          // slice direction(normal) in screen coordinates
          const sliceO = new THREE.Vector3().setFromMatrixPosition(content.sliceCS);
          const sliceZ = new THREE.Vector3().setFromMatrixColumn(content.sliceCS, 2);
          sliceZ.add(sliceO);
          const sliceO2D = this._projector.project(sliceO);
          const sliceZ2D = this._projector.project(sliceZ);
          const sliceDir2D = new THREE.Vector2().subVectors(sliceZ2D, sliceO2D);
          // slice info
          distances.push({
            dist2: dist * dist,
            dir: sliceDir2D
          });
        }
      }
    });
    const rotationFreeAreaRadius2 =
      rotationFreeAreaRadius !== undefined
        ? rotationFreeAreaRadius * rotationFreeAreaRadius
        : this._params.rotationFreeAreaRadius2;
    const axisStickDistanceThreshold2 =
      axisStickDistanceThreshold !== undefined
        ? axisStickDistanceThreshold * axisStickDistanceThreshold
        : this._params.axisStickDistanceThreshold2;
    if (distances.length === 2) {
      if (distances[0].dist2 + distances[1].dist2 < rotationFreeAreaRadius2) {
        if (
          distances[0].dist2 < this._params.crossStickDistanceThreshold2[0] &&
          distances[1].dist2 < this._params.crossStickDistanceThreshold2[1]
        ) {
          pickedType = PickedTypes.CROSS;
        } else {
          if (distances[0].dist2 < axisStickDistanceThreshold2 || distances[1].dist2 < axisStickDistanceThreshold2) {
            pickedType = PickedTypes.UNAVAILABLE;
          } else {
            pickedType = PickedTypes.NONE;
          }
        }
      } else {
        pickedType = PickedTypes.NONE;
        let dir = null;
        if (distances[0].dist2 < axisStickDistanceThreshold2) {
          dir = distances[0].dir;
        } else if (distances[1].dist2 < axisStickDistanceThreshold2) {
          dir = distances[1].dir;
        }
        if (dir) {
          if (Math.abs(dir.x) <= this._epsilon) {
            pickedType = PickedTypes.AXIS_HOR;
          } else if (Math.abs(dir.y) <= this._epsilon) {
            pickedType = PickedTypes.AXIS_VERT;
          } else if (dir.x > this._epsilon && dir.y > this._epsilon) {
            pickedType = PickedTypes.AXIS_RL;
          } else if (dir.x < -this._epsilon && dir.y < -this._epsilon) {
            pickedType = PickedTypes.AXIS_RL;
          } else if (dir.x < -this._epsilon && dir.y > this._epsilon) {
            pickedType = PickedTypes.AXIS_LR;
          } else if (dir.x > this._epsilon && dir.y < -this._epsilon) {
            pickedType = PickedTypes.AXIS_LR;
          }
        } else {
          pickedType = PickedTypes.NONE;
        }
      }
    }
    return pickedType;
  }
}
